import React from 'react';

export function PageHeader({ kicker, title, sub, action }: { kicker: string; title: string; sub?: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
      <div>
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--accent)' }}>{kicker}</div>
        <h1 className="display" style={{ fontSize: 32, fontWeight: 600, margin: '10px 0 6px', letterSpacing: '-0.02em' }}>{title}</h1>
        {sub && <p style={{ color: 'var(--text-2)', fontSize: 14, margin: 0 }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export function Btn({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  const sizes = { sm: { padding: '6px 12px', fontSize: 12 }, md: { padding: '9px 16px', fontSize: 13 } } as const;
  const v = {
    primary: { background: 'var(--accent)', color: 'var(--accent-ink)', border: '1px solid var(--accent)' },
    ghost: { background: 'transparent', color: 'var(--text)', border: '1px solid var(--border-2)' },
    danger: { background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)' },
  } as const;
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...sizes[size], ...v[variant], borderRadius: 10, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      {children}
    </button>
  );
}

export function Card({ children, padding = 20, style }: { children: React.ReactNode; padding?: number; style?: React.CSSProperties }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding, ...style }}>
      {children}
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  step,
  required,
  placeholder,
  fullWidth,
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  required?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
}) {
  return (
    <label style={{ display: 'block', width: fullWidth ? '100%' : undefined }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 6 }}>
        {label.toUpperCase()}
      </div>
      <input
        type={type}
        step={step}
        value={value as string}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%',
          background: 'var(--surface-2)',
          border: '1px solid var(--border-2)',
          color: 'var(--text)',
          padding: '10px 12px',
          borderRadius: 10,
          fontSize: 13,
          outline: 'none',
        }}
      />
    </label>
  );
}

export function Select<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <label style={{ display: 'block' }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 6 }}>
        {label.toUpperCase()}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={{
          width: '100%',
          background: 'var(--surface-2)',
          border: '1px solid var(--border-2)',
          color: 'var(--text)',
          padding: '10px 12px',
          borderRadius: 10,
          fontSize: 13,
          outline: 'none',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Table<T>({
  columns,
  rows,
  onRowClick,
  empty,
}: {
  columns: { key: string; label: string; render: (row: T) => React.ReactNode; width?: number | string }[];
  rows: T[];
  onRowClick?: (row: T) => void;
  empty?: string;
}) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            {columns.map((c) => (
              <th key={c.key} className="mono" style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, letterSpacing: '0.1em', color: 'var(--text-3)', width: c.width }}>
                {c.label.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                {empty ?? 'Sin datos.'}
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr
                key={i}
                onClick={() => onRowClick?.(r)}
                style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)', cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((c) => (
                  <td key={c.key} style={{ padding: '12px 16px' }}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Reusable pagination footer. Hidden when totalPages <= 1. Use:
 *   <Pager total={data.total} page={page} limit={data.limit} onPage={(p) => setSp(...)} />
 */
export function Pager({ total, page, limit, onPage }: { total: number; page: number; limit: number; onPage: (p: number) => void }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  const btn = (disabled: boolean): React.CSSProperties => ({
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    color: disabled ? 'var(--text-3)' : 'var(--text)',
    width: 32, height: 32, borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
  });
  return (
    <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>
        {total} totales · página {page} de {totalPages}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} style={btn(page <= 1)}>←</button>
        <button disabled={page >= totalPages} onClick={() => onPage(page + 1)} style={btn(page >= totalPages)}>→</button>
      </div>
    </div>
  );
}

export function StatCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>{label.toUpperCase()}</div>
      <div className="display mono" style={{ fontSize: 32, fontWeight: 600, marginTop: 8, color: accent ? 'var(--accent)' : 'var(--text)', letterSpacing: '-0.02em' }}>{value}</div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode; footer?: React.ReactNode }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(5,7,12,0.7)', backdropFilter: 'blur(8px)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-2)', border: '1px solid var(--border-2)', borderRadius: 18, padding: 24, width: '100%', maxWidth: 540 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 className="display" style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-2)', width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center' }}>×</button>
        </div>
        {children}
        {footer && <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>{footer}</div>}
      </div>
    </div>
  );
}
